import { type FC, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Link,
  Stack,
  Typography,
} from '@mui/material';

import { useIsMobile } from '@/hooks/useBreakpoints';

const accordionSx = {
  bgcolor: 'background.paper',
  border: '1px solid',
  borderColor: 'border.strong',
  '&:first-of-type': {
    borderRadius: '10px 10px 0 0',
  },
  '&:not(:last-of-type)': {
    borderBottom: 'none',
  },
  '&:last-of-type': {
    borderRadius: '0 0 10px 10px',
  },
  '&:before': { display: 'none' },
  '& .MuiAccordionSummary-root': { minHeight: '52px' },
  '& .MuiAccordionSummary-content': { my: 0 },
};

const FAQ: FC = () => {
  const [expanded, setExpanded] = useState<string[]>([]);

  const isMobile = useIsMobile();

  return (
    <Stack
      sx={{
        gap: { xs: 2, md: 3 },
      }}
    >
      <Typography
        variant={isMobile ? 'h5' : 'h4'}
        sx={{ color: 'neutral.100' }}
      >
        FAQ
      </Typography>
      <Stack>
        <Accordion
          expanded={expanded.includes('faq-0')}
          onChange={(_, nextExpanded) =>
            setExpanded((prev) =>
              nextExpanded
                ? [...prev, 'faq-0']
                : prev.filter((id) => id !== 'faq-0')
            )
          }
          disableGutters
          elevation={0}
          square
          sx={accordionSx}
        >
          <AccordionSummary
            expandIcon={
              expanded.includes('faq-0') ? (
                <RemoveIcon sx={{ color: 'neutral.100' }} />
              ) : (
                <AddIcon sx={{ color: 'neutral.100' }} />
              )
            }
          >
            <Typography variant="body2" sx={{ color: 'neutral.100' }}>
              How do I add an API key?
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack>
              <Typography variant="body1">
                To connect your exchange account:
              </Typography>
              <Stack
                component="ol"
                sx={{ m: 0, pl: 2.5, gap: 0.25, '& li': { fontWeight: 500 } }}
              >
                <Typography component="li" variant="body1">
                  Go to your exchange account settings.
                </Typography>
                <Typography component="li" variant="body1">
                  Create a new API key with trading permissions enabled.
                </Typography>
                <Typography component="li" variant="body1">
                  Copy the API Key and Secret Key.
                </Typography>
                <Typography component="li" variant="body1">
                  Paste them in the API key section and save.
                </Typography>
              </Stack>
              <Typography variant="body1" sx={{ mt: 1 }}>
                For a more detailed explanation, visit:
              </Typography>
              <Link
                href="https://docs.hu.finance/campaign-participation/api-keys/"
                target="_blank"
                rel="noreferrer"
                variant="body1"
              >
                https://docs.hu.finance/campaign-participation/api-keys/
              </Link>
            </Stack>
          </AccordionDetails>
        </Accordion>
        <Accordion
          expanded={expanded.includes('faq-1')}
          onChange={(_, nextExpanded) =>
            setExpanded((prev) =>
              nextExpanded
                ? [...prev, 'faq-1']
                : prev.filter((id) => id !== 'faq-1')
            )
          }
          disableGutters
          elevation={0}
          square
          sx={accordionSx}
        >
          <AccordionSummary
            expandIcon={
              expanded.includes('faq-1') ? (
                <RemoveIcon sx={{ color: 'neutral.100' }} />
              ) : (
                <AddIcon sx={{ color: 'neutral.100' }} />
              )
            }
          >
            <Typography variant="body2" sx={{ color: 'neutral.100' }}>
              How do I stake HMT?
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack>
              <Typography variant="body1">To stake your HMT tokens:</Typography>
              <Stack
                component="ol"
                sx={{ m: 0, pl: 2.5, gap: 0.25, '& li': { fontWeight: 500 } }}
              >
                <Typography component="li" variant="body1">
                  Connect your wallet.
                </Typography>
                <Typography component="li" variant="body1">
                  Navigate to the Staking section.
                </Typography>
                <Typography component="li" variant="body1">
                  Enter the amount of HMT you want to stake.
                </Typography>
                <Typography component="li" variant="body1">
                  Confirm the transaction in your wallet.
                </Typography>
              </Stack>
              <Typography variant="body1" sx={{ mt: 1 }}>
                For a more detailed explanation, visit:
              </Typography>
              <Link
                href="https://docs.hu.finance/campaign-creation/#1-staking"
                target="_blank"
                rel="noreferrer"
                variant="body1"
              >
                https://docs.hu.finance/campaign-creation/#1-staking
              </Link>
            </Stack>
          </AccordionDetails>
        </Accordion>
        <Accordion
          expanded={expanded.includes('faq-2')}
          onChange={(_, nextExpanded) =>
            setExpanded((prev) =>
              nextExpanded
                ? [...prev, 'faq-2']
                : prev.filter((id) => id !== 'faq-2')
            )
          }
          disableGutters
          elevation={0}
          square
          sx={accordionSx}
        >
          <AccordionSummary
            expandIcon={
              expanded.includes('faq-2') ? (
                <RemoveIcon sx={{ color: 'neutral.100' }} />
              ) : (
                <AddIcon sx={{ color: 'neutral.100' }} />
              )
            }
          >
            <Typography variant="body2" sx={{ color: 'neutral.100' }}>
              How are campaign rewards distributed?
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack>
              <Typography variant="body1">
                Campaign rewards are distributed based on the trading
                performance and volume generated during the campaign period.
                Once the campaign ends, eligible participants will receive
                rewards directly to their connected wallet.
              </Typography>
              <Typography variant="body1" sx={{ mt: 1 }}>
                For a more detailed explanation, visit:
              </Typography>
              <Link
                href="https://docs.hu.finance/holding/#reward-distribution"
                target="_blank"
                rel="noreferrer"
                variant="body1"
              >
                https://docs.hu.finance/holding/#reward-distribution
              </Link>
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Stack>
    </Stack>
  );
};

export default FAQ;
